import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { Visibility } from "@mui/icons-material";

import FormCheckboxGroup from "qubCommon/Inputs/CheckboxGroup";
import TextInput from "qubCommon/Inputs/TextInput";
import Dropdown from "qubCommon/Inputs/Dropdown";
import DateInput from "qubCommon/Inputs/DateInput";

import PreviewTablePayment from "./PreviewTablePayment";
import PaymentConfirmDialog from "./PaymentConfirmDialog";

import {
  createNewPayment,
  getPaymentDecisionAppTypes,
  getPaymentDecisionSummaryFromAppTypes,
} from "apis/controllers/clearings";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";


const NewPaymentDialog = ({ open, onClose, fetchClearings, seasonId }) => {
  const defaultValues = {
    paymentApplications: [],
    selectedPayments: [],
    date: null,
    decisionNo: "",
    signer: "",
  };


  const validationSchema = yup.object({
    paymentApplications: yup
      .array()
      .of(yup.number().required())
      .min(1, "Πρέπει να επιλέξετε τουλάχιστον έναν τύπο αίτησης."),



    decisionNo: yup
      .string()
      .trim()
      .required("Το πεδίο 'Αρ. Απόφασης' είναι υποχρεωτικό."),

    decisionDate: yup
      .date()
      .typeError("Εισάγετε έγκυρη ημερομηνία απόφασης.")
      .required("Η ημερομηνία απόφασης είναι υποχρεωτική."),

    paymentDate: yup
      .date()
      .typeError("Εισάγετε έγκυρη ημερομηνία πληρωμής.")
      .required("Η ημερομηνία πληρωμής είναι υποχρεωτική."),

    signer: yup
      .string()
      .required("Ο/Η υπογράφων/ουσα διατάκτης είναι υποχρεωτικός."),
  });

  const methods = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const selectedTypes = useWatch({ name: "paymentApplications", control: methods.control });

  const [applicationTypes, setApplicationTypes] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [signers, setSigners] = useState([]);
  const [saving, setSaving] = useState(false);

  // ============================
  // Fetch Application Types
  // ============================
  useEffect(() => {
    const fetchAppTypes = async () => {
      setLoadingTypes(true);
      setError(null);
      try {
        const response = await getPaymentDecisionAppTypes({ seasonId: seasonId ?? 2025 });
        setApplicationTypes(response?.data || []);
      } catch (err) {
        console.error("Error loading app types:", err);
        setError("Αποτυχία φόρτωσης τύπων αιτήσεων.");
      } finally {
        setLoadingTypes(false);
      }
    };

    if (open) fetchAppTypes();
  }, [open]);

  // reset local form when dialog opens/closes
  useEffect(() => {
    if (open) {
      methods.reset(defaultValues);
      setPreviewData([]);
      setError(null);
      setUpdateError(null);
    }
  }, [open]); // eslint-disable-line

  // ============================
  // Preview Data
  // ============================
  const handlePreview = async () => {
    if (!selectedTypes?.length) return;

    setLoadingPreview(true);
    setError(null);
    try {
      const response = await getPaymentDecisionSummaryFromAppTypes({
        seasonId: seasonId ?? 2025,
        AppTypes: selectedTypes,
      });

      setPreviewData(response?.data?.types || []);
      setSigners(response?.data?.employees || []);
    } catch (err) {
      console.error("Error loading preview data:", err);
      setError("Αποτυχία φόρτωσης προεπισκόπησης πληρωμών.");
    } finally {
      setLoadingPreview(false);
    }
  };

  // ============================
  // Create Payment
  // ============================
  const handleUpdate = async () => {
    setSaving(true);
    setUpdateError(null);
    const data = methods.getValues();

    try {
      const res = await createNewPayment({
        seasonId: seasonId ?? 2025,
        AppTypeIds: data?.selectedPayments?.map((item) => item.typeId),
        DecisionDate: data?.decisionDate,
        PaymentDate: data?.paymentDate,
        DecisionNumber: data?.decisionNo,
        SignedBy: data?.signer,
      });

      if (!res || res?.status === "error") {
        throw new Error(res?.message || "Αποτυχία δημιουργίας πληρωμής.");
      }

      // success → close and reset
      if (fetchClearings) fetchClearings();
      setOpenConfirm(false);
      methods.reset(defaultValues);
      setPreviewData([]);
      onClose();
    } catch (err) {
      console.error("Error creating new payment:", err?.message);
      setUpdateError(err?.message || "Αποτυχία δημιουργίας πληρωμής. Παρακαλώ δοκιμάστε ξανά.");
    } finally {
      setOpenConfirm(false);
      setSaving(false);
    }
  };

  const handleValidateAndOpenConfirm = async () => {
    const isValid = await methods.trigger(); // manually trigger validation
    if (isValid) {
      setOpenConfirm(true);
    }
  };

  // ============================
  // UI Rendering
  // ============================
  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          methods.reset(defaultValues);
          setPreviewData([]);
          onClose();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle variant="h4">Νέα Πληρωμή</DialogTitle>
        <DialogContent dividers>
          <FormProvider {...methods}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {loadingTypes ? (
                <Stack alignItems="center" justifyContent="center" py={3}>
                  <CircularProgress size={28} />
                </Stack>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : (
                <FormCheckboxGroup
                  name="paymentApplications"
                  options={applicationTypes}
                  groupLabel="Επιλέξτε Τύπους Αιτήσεων"
                  labelName="name"
                  valueName="id"
                />
              )}

              <Button
                variant="outlined"
                onClick={handlePreview}
                disabled={!selectedTypes?.length || loadingPreview}
                startIcon={
                  loadingPreview ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Visibility />
                  )
                }
              >
                {loadingPreview ? "Φόρτωση..." : "Προεπισκόπηση"}
              </Button>

              {previewData.length > 0 && (
                <>
                  <PreviewTablePayment
                    rows={previewData}
                    onSelectionChange={(selected) =>
                      methods.setValue("selectedPayments", selected)
                    }
                  />

                  <Grid container spacing={2} mt={2}>
                    <Grid item xs={12} sm={6}>
                      <TextInput name="decisionNo" label="Αρ. Απόφασης" isFullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DateInput name="decisionDate" label="Ημερομηνία Απόφασης" isFullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <DateInput name="paymentDate" label="Ημερομηνία Πληρωμής(Πίστωσης)" isFullWidth />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Dropdown
                        name="signer"
                        label="Υπογράφων/ουσα Διατάκτης"
                        options={signers}
                        isFullWidth
                        labelName="name"
                        valueName="id"
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </Stack>
            {/* Error alert shown here */}
            {updateError && (
              <Stack px={3} mt={2}>
                <Alert severity="error">{updateError}</Alert>
              </Stack>
            )}

          </FormProvider>
        </DialogContent>


        <DialogActions>
          <Button
            color='error'
            variant='contained'
            onClick={() => {
              methods.reset(defaultValues);
              setPreviewData([]);
              onClose();
            }}
          >
            Άκυρο
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={previewData.length === 0 || saving}
            onClick={handleValidateAndOpenConfirm}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Αποθήκευση..." : "Ενημέρωση"}
          </Button>

        </DialogActions>
      </Dialog>

      <PaymentConfirmDialog
        open={openConfirm}
        saving={saving}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleUpdate}
      />
    </>
  );
};

export default NewPaymentDialog;
